import { MenuLink } from '../MenuLink/MenuLink.component';
import React, { ComponentPropsWithRef, FC, PropsWithChildren, useMemo } from 'react';
import ThematicNav from '@components/ThematicNav/ThematicNav.component';
import { useWeatherLink } from '@meteo/business/meteoUrl.utils';
import { SettingsTabEnum } from '@settings/business/Settings.type';
import { StocksHomeTabs } from '@stocks/business/Stocks.types';
import { usePlcRouter } from '@utils/customHooks';
import { getLinkProps, getPreferencesLinkProps, urlEnum } from '@utils/url';
import classnames from 'classnames';

interface Props extends ComponentPropsWithRef<'nav'> {
  isMobile?: boolean;
  isSSO?: boolean;
  className?: string;
}

const Navigation: FC<PropsWithChildren<Props>> = ({ className, children, isSSO, isMobile = false, ...rest }) => {
  const { route } = usePlcRouter();
  const isSSOclassname = 'is-sso space-between align-center width-100';
  const rootClassName = classnames('navigation', 'flex', isSSO ? isSSOclassname : '', className);
  const menuLinkClassName = 'header-navigation';
  const variant = isMobile ? 'h5' : undefined;
  const meteoLinkProps = useWeatherLink(urlEnum.meteo);
  const radarLinkProps = useWeatherLink(urlEnum.meteoRadar);
  const hourByHourLinkProps = useWeatherLink(urlEnum.hourByHour);
  const treatmentScheduleLinkProps = useWeatherLink(urlEnum.treatmentSchedule);
  const meteoPreferencesLinkProps = useMemo(() => getPreferencesLinkProps(SettingsTabEnum.forecast), []);

  return (
    <nav className={rootClassName} {...rest}>
      {isSSO ? (
        <>
          <div className="flex row">
            <MenuLink
              className={menuLinkClassName}
              i18nKey="common.the-weather"
              linkProps={meteoLinkProps}
              variant={variant}
            />
            <MenuLink
              className={menuLinkClassName}
              i18nKey="meteo.radar.breadcrumb"
              linkProps={radarLinkProps}
              variant={variant}
            />
            <MenuLink
              className={menuLinkClassName}
              i18nKey="meteo.hour-by-hour.menu-title"
              linkProps={hourByHourLinkProps}
              variant={variant}
            />
            <MenuLink
              className={menuLinkClassName}
              i18nKey="treatment-schedule.page.title"
              linkProps={treatmentScheduleLinkProps}
              variant={variant}
            />
            <MenuLink
              className={menuLinkClassName}
              i18nKey="services.dryingHerbs.menu-title"
              linkProps={{ as: urlEnum.herbDrying, href: urlEnum.herbDrying }}
              variant={variant}
            />
          </div>
          <MenuLink
            className={menuLinkClassName}
            i18nKey="preferences.menu-title.meteo-preferences"
            linkProps={meteoPreferencesLinkProps}
            variant={variant}
          />
        </>
      ) : (
        <>
          <MenuLink
            active={urlEnum.home === route}
            className={menuLinkClassName}
            i18nKey="common.newspaper"
            linkProps={{ as: urlEnum.home, href: urlEnum.home }}
            variant={variant}
          />
          <MenuLink
            active={route !== urlEnum.home && (urlEnum.vraijournal.includes(route) || route.includes(urlEnum.actu))}
            className={menuLinkClassName}
            i18nKey="common.journal"
            linkProps={{ as: urlEnum.vraijournal, href: urlEnum.vraijournal }}
            variant={variant}
          />
          <MenuLink
            active={RegExp(urlEnum.meteo).test(route)}
            className={menuLinkClassName}
            i18nKey="common.the-weather"
            linkProps={meteoLinkProps}
            variant={variant}
          />
          <MenuLink
            active={RegExp(urlEnum.stocks).test(route)}
            className={menuLinkClassName}
            i18nKey="common.stocks.menu-title"
            linkProps={getLinkProps(`${urlEnum.stocks}/[stockType]`, { stockType: StocksHomeTabs.Crops })}
            variant={variant}
          />
          <MenuLink
            active={RegExp(urlEnum.services).test(route)}
            className={menuLinkClassName}
            i18nKey="common.services.menu-title"
            linkProps={{ as: urlEnum.services, href: urlEnum.services }}
            variant={variant}
          />
          <MenuLink
            active={RegExp(urlEnum.guides).test(route)}
            className={menuLinkClassName}
            i18nKey="common.guides"
            linkProps={{ as: urlEnum.guides, href: urlEnum.guides }}
            variant={variant}
          />
          <MenuLink
            active={RegExp(urlEnum.podcasts).test(route)}
            className={menuLinkClassName}
            i18nKey="common.podcasts"
            linkProps={{ as: urlEnum.podcasts, href: urlEnum.podcasts }}
            variant={variant}
          />
          {isMobile && <ThematicNav isSmartphoneOrTablet={isMobile} />}
        </>
      )}
      {children}
    </nav>
  );
};

export { Navigation };
